import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/useAuth';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const { isRecovery, clearRecovery } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // If the user navigates here directly without a recovery session, redirect to login
  useEffect(() => {
    if (!isRecovery) {
      // Small delay to allow the auth state to settle after URL hash processing
      const timeout = setTimeout(() => {
        if (!isRecovery) {
          navigate('/login', { replace: true });
        }
      }, 2000);
      return () => clearTimeout(timeout);
    }
  }, [isRecovery, navigate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password,
    });

    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }

    setSuccess(true);
    setLoading(false);
    clearRecovery();

    // Redirect to dashboard after a short delay
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 2000);
  }

  if (!isRecovery && !success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <p className="text-sm text-text-muted">Verifying recovery link...</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="w-full max-w-sm">
        {/* Logo / Branding */}
        <div className="mb-8 text-center">
          <img src="/images/logo-black.svg" alt="Ellie" className="h-10 w-auto mx-auto" />
          <p className="mt-2 text-sm text-text-muted">Admin Dashboard</p>
        </div>

        {/* Card */}
        <div className="rounded-xl border border-border bg-white px-6 py-8 shadow-sm">
          {success ? (
            <div className="text-center">
              <h2 className="mb-2 text-lg font-semibold text-text-primary">Password updated</h2>
              <p className="text-sm text-text-muted">Redirecting you to the dashboard...</p>
            </div>
          ) : (
            <>
              <h2 className="mb-2 text-center text-lg font-semibold text-text-primary">
                Set new password
              </h2>
              <p className="mb-6 text-center text-sm text-text-muted">
                Enter your new password below.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* New password */}
                <div>
                  <label
                    htmlFor="password"
                    className="mb-1 block text-sm font-medium text-text-primary"
                  >
                    New password
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
                    placeholder="At least 8 characters"
                  />
                </div>

                {/* Confirm password */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="mb-1 block text-sm font-medium text-text-primary"
                  >
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    required
                    minLength={8}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-lg border border-border px-3 py-2 text-sm text-text-primary outline-none transition-colors focus:border-brand focus:ring-1 focus:ring-brand"
                    placeholder="Re-enter your password"
                  />
                </div>

                {/* Error message */}
                {error && (
                  <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={loading || !password || !confirmPassword}
                  className="w-full rounded-lg bg-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </form>

              <p className="mt-4 text-center text-sm text-text-muted">
                <Link to="/login" className="font-medium text-brand hover:text-brand-dark">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
